import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, AlertCircle, Check, RefreshCw, ZoomIn } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

// Maximum file size in bytes (500KB)
const MAX_FILE_SIZE = 500 * 1024;
// Accepted image formats
const ACCEPTED_FORMATS = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
// Maximum dimensions for responsive images
const MAX_WIDTH = 1920;
const MAX_HEIGHT = 1080;

/**
 * Compress an image file to meet size requirements
 * @param {File} file - The original image file
 * @param {number} maxSize - Maximum size in bytes
 * @returns {Promise<{blob: Blob, dataUrl: string, originalSize: number, compressedSize: number}>}
 */
const compressImage = async (file, maxSize = MAX_FILE_SIZE) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        
        // Calculate new dimensions maintaining aspect ratio
        if (width > MAX_WIDTH) {
          height = (height * MAX_WIDTH) / width;
          width = MAX_WIDTH;
        }
        if (height > MAX_HEIGHT) {
          width = (width * MAX_HEIGHT) / height;
          height = MAX_HEIGHT;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Start with high quality and reduce until size is acceptable
        let quality = 0.9;
        let dataUrl = canvas.toDataURL('image/jpeg', quality);
        
        // Iteratively reduce quality until file size is under maxSize
        while (dataUrl.length * 0.75 > maxSize && quality > 0.1) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL('image/jpeg', quality);
        }
        
        // Convert data URL to Blob
        const byteString = atob(dataUrl.split(',')[1]);
        const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        
        const blob = new Blob([ab], { type: mimeString });
        
        resolve({
          blob,
          dataUrl,
          originalSize: file.size,
          compressedSize: blob.size,
          width,
          height,
          quality: Math.round(quality * 100)
        });
      };
      
      img.onerror = () => reject(new Error('Error al cargar la imagen'));
    };
    
    reader.onerror = () => reject(new Error('Error al leer el archivo'));
  });
};

/**
 * Format bytes to human readable string
 */
const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * ImageUploader Component
 * Handles multiple image uploads with automatic compression
 */
const ImageUploader = ({ 
  images = [], 
  onChange, 
  maxImages = 10, 
  maxSizeKB = 500,
  disabled = false,
  showPreview = true,
  className = ""
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [compressionStats, setCompressionStats] = useState([]);
  const fileInputRef = useRef(null);

  const handleFiles = useCallback(async (files) => {
    if (disabled) return;
    
    const validFiles = Array.from(files).filter(file => {
      if (!ACCEPTED_FORMATS.includes(file.type)) {
        toast.error(`Formato no soportado: ${file.name}. Use JPG, PNG o WebP.`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    // Check max images limit
    const remainingSlots = maxImages - images.length;
    if (remainingSlots <= 0) {
      toast.error(`Máximo ${maxImages} imágenes permitidas`);
      return;
    }

    const filesToProcess = validFiles.slice(0, remainingSlots);
    if (filesToProcess.length < validFiles.length) {
      toast.warning(`Solo se procesarán ${filesToProcess.length} de ${validFiles.length} imágenes (límite: ${maxImages})`);
    }

    setUploading(true);
    const newImages = [];
    const newStats = [];

    for (const file of filesToProcess) {
      try {
        const result = await compressImage(file, maxSizeKB * 1024);
        
        newImages.push({
          id: `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          dataUrl: result.dataUrl,
          blob: result.blob,
          name: file.name,
          originalSize: result.originalSize,
          compressedSize: result.compressedSize,
          width: result.width,
          height: result.height
        });

        newStats.push({
          name: file.name,
          original: result.originalSize,
          compressed: result.compressedSize,
          reduction: Math.round((1 - result.compressedSize / result.originalSize) * 100)
        });

        // Show compression info
        if (result.originalSize > result.compressedSize) {
          toast.success(
            `${file.name}: Comprimida de ${formatBytes(result.originalSize)} a ${formatBytes(result.compressedSize)}`,
            { duration: 3000 }
          );
        }
      } catch (error) {
        console.error('Error compressing image:', error);
        toast.error(`Error al procesar ${file.name}`);
      }
    }

    setCompressionStats(prev => [...prev, ...newStats]);
    onChange([...images, ...newImages]);
    setUploading(false);
  }, [images, onChange, maxImages, maxSizeKB, disabled]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const removeImage = (imageId) => {
    onChange(images.filter(img => img.id !== imageId));
    toast.info('Imagen eliminada');
  };

  const openPreview = (image) => {
    setPreviewImage(image);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Drop Zone */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 transition-all ${
          dragActive 
            ? 'border-primary bg-primary/5' 
            : 'border-slate-300 hover:border-primary/50'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED_FORMATS.join(',')}
          onChange={handleInputChange}
          className="hidden"
          disabled={disabled}
        />
        
        <div className="flex flex-col items-center justify-center text-center">
          {uploading ? (
            <>
              <RefreshCw className="w-12 h-12 text-primary animate-spin mb-3" />
              <p className="text-slate-600 font-medium">Procesando imágenes...</p>
            </>
          ) : (
            <>
              <Upload className="w-12 h-12 text-slate-400 mb-3" />
              <p className="text-slate-600 font-medium mb-1">
                Arrastra y suelta tus imágenes aquí
              </p>
              <p className="text-sm text-slate-400">
                o haz clic para seleccionar archivos
              </p>
              <div className="flex flex-wrap gap-2 mt-3 justify-center">
                <Badge variant="outline" className="text-xs">JPG, PNG, WebP</Badge>
                <Badge variant="outline" className="text-xs">Máx. {maxSizeKB}KB por imagen</Badge>
                <Badge variant="outline" className="text-xs">Hasta {maxImages} imágenes</Badge>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg text-sm">
        <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
        <div className="text-blue-700">
          <p className="font-medium">Compresión automática activada</p>
          <p className="text-blue-600">Las imágenes se ajustarán automáticamente a {maxSizeKB}KB máximo para optimizar la carga.</p>
        </div>
      </div>

      {/* Image Previews */}
      {showPreview && images.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-700">
              Imágenes cargadas ({images.length}/{maxImages})
            </p>
            {images.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-red-500 hover:text-red-600"
                onClick={() => onChange([])}
              >
                Eliminar todas
              </Button>
            )}
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {images.map((image, index) => (
              <Card 
                key={image.id} 
                className="relative group overflow-hidden aspect-square"
              >
                <img
                  src={image.dataUrl || image}
                  alt={image.name || `Imagen ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                
                {/* Overlay with actions */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-8 w-8 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      openPreview(image);
                    }}
                  >
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="h-8 w-8 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeImage(image.id);
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                {/* First image indicator */}
                {index === 0 && (
                  <Badge className="absolute top-2 left-2 bg-primary text-xs">
                    Principal
                  </Badge>
                )}

                {/* Size info */}
                {image.compressedSize && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 text-center">
                    {formatBytes(image.compressedSize)}
                    {image.originalSize > image.compressedSize && (
                      <span className="text-green-400 ml-1">
                        (-{Math.round((1 - image.compressedSize / image.originalSize) * 100)}%)
                      </span>
                    )}
                  </div>
                )}
              </Card>
            ))}

            {/* Add more placeholder */}
            {images.length < maxImages && (
              <Card 
                className="aspect-square border-2 border-dashed border-slate-300 hover:border-primary/50 flex items-center justify-center cursor-pointer transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="text-center text-slate-400">
                  <ImageIcon className="w-8 h-8 mx-auto mb-1" />
                  <span className="text-xs">Agregar</span>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Compression Stats */}
      {compressionStats.length > 0 && (
        <div className="p-3 bg-green-50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Check className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-700">Resumen de compresión</span>
          </div>
          <div className="text-xs text-green-600 space-y-1">
            {compressionStats.slice(-3).map((stat, i) => (
              <p key={i}>
                {stat.name}: {formatBytes(stat.original)} → {formatBytes(stat.compressed)} 
                <span className="font-medium"> ({stat.reduction}% reducido)</span>
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{previewImage?.name || 'Vista previa'}</DialogTitle>
          </DialogHeader>
          {previewImage && (
            <div className="space-y-4">
              <img
                src={previewImage.dataUrl || previewImage}
                alt={previewImage.name}
                className="w-full max-h-[60vh] object-contain rounded-lg"
              />
              {previewImage.width && (
                <div className="flex gap-4 text-sm text-slate-500 justify-center">
                  <span>Dimensiones: {previewImage.width} x {previewImage.height}px</span>
                  {previewImage.compressedSize && (
                    <span>Tamaño: {formatBytes(previewImage.compressedSize)}</span>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImageUploader;
